import { useEffect, useState } from 'react'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { Card } from '../../ui/Card'
import { Copyable } from '../../ui/Formatting'
import { Money } from '../../ui/Money'
import { isExclusive, isHostProduct, isQuoteLive, quoteBlocksRemaining } from '../../core/rental'
import { reviewFacts, type RentalDraft } from '../../core/rentalDraft'
import { Rate } from '../../ui/Rate'
import type { DepositCheck } from '../../core/deposit'
import { BLOCK_SECONDS, formatUsdMicros, type PriceCoin } from '../../core/pricing'
import { amountForRunTime, formatHold, formatStopTime, RUN_TIME_CHOICES, runTimeReadout, type RunTimeKey } from '../../core/runTime'
import type { SizeValues } from '../../core/sizing'
import { toMicroAmount } from '../../core/fees'
import { EscrowSteps, PrepayNotice, ResourceTiles, RunTimeReadoutView } from './reviewParts'
import type { GuidedPort } from '../../core/guided'
import { DIRECT_ALLOWLIST_LINE, PortAccessReview } from '../configure/PortAccessReview'

/**
 * The review screen for a direct rental.
 *
 * Gate 4 requires every review screen to state provider binding, resource
 * guarantee, shared/exclusive status, exact price, expiry, deposit and refund.
 * All seven come from `reviewFacts`, whose fields are non-optional precisely so
 * a screen cannot render five of them and look finished. Shared/exclusive and
 * the machine binding are rows only for a host product: custom capacity binds
 * no machine, and the provider line below says so instead of a row reading
 * "Not applicable".
 *
 * The deposit is chosen as run time (RENT-RUNTIME-PLAN W1). A beta tenant
 * approved the old $0.50 default, about half an hour, and only learned what
 * it meant when the lease closed. So the choice is a duration, the default is
 * never under an hour, and a run under an hour has to be confirmed.
 *
 * The screen's other job: a quote expires. It is a signed commitment with a
 * block deadline, and the two states either side of that deadline need
 * different screens, not a disabled button with a stale price still on it.
 */

export type RunTimeChoice = RunTimeKey | 'custom' | null

export interface RunTimeState {
  /** The selected duration, 'custom' while the tenant types an amount, or null for none. */
  choice: RunTimeChoice
  onChoice: (next: RunTimeKey) => void
  /** The typed dollar amount, used when `choice` is 'custom'. */
  custom: string
  onCustom: (next: string) => void
  /** The check of the amount that will be sent, whichever way it was chosen. */
  check: DepositCheck
  /** What the wallet can put in, in `uddt` (its whole balance: no fee is held back); null when unknown. */
  available?: bigint | null | undefined
  /** The tenant confirmed a run under an hour, for exactly this amount. */
  acknowledged: boolean
  onAcknowledge: (next: boolean) => void
}

export interface DirectRentalReviewProps {
  draft: RentalDraft
  /** Current chain height. Undefined means unknown — see below. */
  height?: number | undefined
  /** Resource summary, rendered as given when `size` is absent. */
  resources: string
  /** The requested size, shown as CPU, memory and storage tiles. */
  size?: SizeValues | undefined
  /** Chain pricing denomination used by the signed quote. */
  priceDenom?: string | undefined
  /** Refuses approval when the live quote exceeds the posted SDL ceiling. */
  priceError?: string | null | undefined
  busy?: boolean
  onApprove?: (() => void) | undefined
  onRequote?: (() => void) | undefined
  onBack?: (() => void) | undefined
  /** The prepaid run time. Absent, the chain minimum is sent and no choice is shown. */
  runTime?: RunTimeState | undefined
  /** The guided ports; restricted ones are listed under the provider. */
  ports?: GuidedPort[] | undefined
  /** Disables approval for a reason the page explains itself, without the busy label. */
  approvalBlocked?: boolean | undefined
}

/**
 * Seconds left on the held price, ticking between chain heights.
 *
 * The chain height moves every ~6 s and is only polled, so the countdown is
 * anchored at the moment the current height was first seen and counts down
 * locally from there; each new height re-anchors it.
 */
function useHoldSeconds(blocks: number, height: number | undefined, live: boolean): number {
  const [anchor, setAnchor] = useState(() => ({ height, at: Date.now() }))
  if (anchor.height !== height) setAnchor({ height, at: Date.now() })
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!live) return undefined
    const id = window.setInterval(() => setTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [live])
  return blocks * BLOCK_SECONDS - (Date.now() - anchor.at) / 1000
}

function RunTimePicker({ runTime, rate, now }: { runTime: RunTimeState; rate: PriceCoin | null; now: number }) {
  const readout = runTime.check.ok && rate ? runTimeReadout(runTime.check.micro, rate, now) : null
  // A typed amount reports every problem; a chosen duration can only be
  // refused for the wallet, and must say so rather than disable silently.
  const error = !runTime.check.ok && runTime.check.reason !== 'empty' && (runTime.choice === 'custom' || runTime.check.reason === 'above-balance')
    ? runTime.check.message
    : undefined
  const available = runTime.available
  return <>
    <PrepayNotice />
    {rate && <div className="runway-choices" role="radiogroup" aria-label="Run time">
      {RUN_TIME_CHOICES.map((choice) => {
        const dollars = amountForRunTime(rate, choice.hours)
        const micro = dollars === null ? null : toMicroAmount(dollars)
        const unaffordable = available !== null && available !== undefined && micro !== null && BigInt(micro) > available
        return <button key={choice.key} type="button" role="radio" aria-checked={runTime.choice === choice.key} className={`runway-choice${unaffordable ? ' runway-choice--over' : ''}`} onClick={() => runTime.onChoice(choice.key)}>
          <span className="runway-choice-label">{choice.label}</span>
          {dollars && <span className="runway-choice-amount">${dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>}
          {unaffordable && <span className="runway-choice-note">More than your wallet holds</span>}
        </button>
      })}
    </div>}
    {error && runTime.choice !== 'custom' && <div className="field-error" role="alert">{error}</div>}
    <div className="field">
      <label htmlFor="direct-deposit-amount">Or enter a deposit</label>
      <span className="money-input">
        <span aria-hidden="true">$</span>
        <input id="direct-deposit-amount" value={runTime.choice === 'custom' ? runTime.custom : ''} inputMode="decimal" autoComplete="off" placeholder="0.00" aria-invalid={error && runTime.choice === 'custom' ? true : undefined} aria-describedby={error && runTime.choice === 'custom' ? 'direct-deposit-amount-error' : undefined} onChange={(event) => runTime.onCustom(event.target.value.replace(/[^0-9.]/g, ''))} />
      </span>
      {error && runTime.choice === 'custom' && <div className="field-error" id="direct-deposit-amount-error" role="alert">{error}</div>}
    </div>
    {readout && <RunTimeReadoutView readout={readout}>
      {readout.short && <>
        <p className="runtime-readout-warning">Under an hour. When this runs out the lease closes and the instance stops.</p>
        <label className="runtime-ack">
          <input type="checkbox" checked={runTime.acknowledged} onChange={(event) => runTime.onAcknowledge(event.target.checked)} />
          I understand it stops in about {readout.duration}
        </label>
      </>}
    </RunTimeReadoutView>}
    <EscrowSteps />
  </>
}

export function DirectRentalReview({
  draft,
  height,
  resources,
  size,
  priceDenom,
  priceError,
  busy = false,
  onApprove,
  onRequote,
  onBack,
  runTime,
  ports = [],
  approvalBlocked = false,
}: DirectRentalReviewProps) {
  const facts = reviewFacts(draft, height ?? 0)
  const quote = draft.quote
  const denom = priceDenom ?? 'uddt'
  const rate: PriceCoin | null = quote ? { denom, amount: quote.pricePerBlock } : null

  // An unknown height cannot be treated as "fresh". Without it the console
  // cannot tell a live quote from an expired one, and the safe reading of "I
  // do not know" is not "it is fine" — signing an expired quote wastes a
  // wallet approval on a transaction the chain will reject.
  const heightKnown = typeof height === 'number' && height > 0
  const live = Boolean(quote) && heightKnown && isQuoteLive(quote!, height!)
  const blocks = quote && heightKnown ? quoteBlocksRemaining(quote, height) : 0
  const holdSeconds = useHoldSeconds(blocks, height, live)
  const holdOver = live && holdSeconds <= 0

  const now = Date.now()
  const amount = runTime?.check.ok ? runTime.check.micro : null
  const readout = amount !== null && rate ? runTimeReadout(amount, rate, now) : null
  const needsAck = Boolean(readout?.short) && !runTime?.acknowledged
  const label = busy
    ? 'Waiting for your wallet…'
    : !runTime
      ? 'Approve and rent'
      : amount === null
        ? !runTime.check.ok && runTime.check.reason === 'above-balance' ? 'Not enough in your wallet' : 'Choose a run time'
        : needsAck
          ? 'Confirm the short run time'
          : readout
            ? `Approve ${formatUsdMicros(BigInt(amount))} for ${readout.duration}`
            : 'Approve and rent'
  const blocked = busy || approvalBlocked || holdOver || (runTime !== undefined && (amount === null || needsAck))

  return (
    <section className="direct-rental-review rent-review">
      <div className="rent-review-main">
        <Card title="What you are renting">
          <ResourceTiles size={size} resources={resources} />
          <dl className="review-list">
            <div>
              <dt>Provider</dt>
              <dd>
                {draft.provider ? <Copyable value={draft.provider} buttonLabel="Copy provider address" /> : facts.providerBinding}
                <p className="field-help">
                  {isHostProduct(draft.mode)
                    ? 'Only this provider can take the lease, on the machine below.'
                    : 'Only this provider can take the lease. It may place it on any of its machines.'}
                </p>
              </dd>
            </div>
            {isHostProduct(draft.mode) ? (
              <>
                <div>
                  <dt>Machine</dt>
                  <dd>
                    {facts.hostBinding}{' '}
                    <Badge tone={isExclusive(draft.mode) ? 'positive' : 'neutral'}>
                      {isExclusive(draft.mode) ? 'Exclusive' : 'Shared'}
                    </Badge>
                  </dd>
                </div>
                <div>
                  <dt>Who else runs here</dt>
                  <dd>{facts.occupancy}</dd>
                </div>
              </>
            ) : null}
            <PortAccessReview ports={ports} hostLine={DIRECT_ALLOWLIST_LINE} />
          </dl>
        </Card>

        {runTime ? (
          <Card header={<><h2>How long should it run?</h2>{rate ? <span className="rent-review-rate"><Rate coin={rate} /></span> : null}</>}>
            <RunTimePicker runTime={runTime} rate={rate} now={now} />
          </Card>
        ) : null}

        <details className="ui-card ui-card-elev-1 rent-review-signing">
          <summary>What you are signing</summary>
          {/*
            Stated because it is the product's main claim and a tenant cannot
            verify it from a wallet prompt: the deployment and the lease are one
            transaction, so a rejected quote leaves nothing behind.
          */}
          <p className="muted">
            One approval. The deployment and the lease are created in a single
            transaction — if the provider's quote is rejected, nothing is created
            and nothing is charged.
          </p>
          <dl className="review-list">
            <div>
              <dt>Price held until</dt>
              <dd>{facts.expiry}</dd>
            </div>
          </dl>
        </details>
      </div>

      <aside className="rent-review-summary" aria-label="Summary">
        <Card title="Summary">
          <dl className="review-list">
            <div>
              <dt>Price</dt>
              <dd>
                {/*
                  "Not quoted yet" rather than "No price yet": the notice below
                  owns the call to action, and two elements saying the same
                  phrase made the actionable one ambiguous to a screen reader
                  scanning by text — and to a test.
                */}
                {rate ? <Rate coin={rate} /> : 'Not quoted yet'}
                <p className="field-help">{facts.priceMeaning}</p>
              </dd>
            </div>
            {runTime ? (
              <>
                <div>
                  <dt>Prepaid run time (escrow)</dt>
                  <dd>{amount !== null ? <Money coin={{ denom, amount }} /> : '—'}</dd>
                </div>
                <div>
                  <dt>Runs for about</dt>
                  <dd className={readout?.short ? 'runtime-short' : undefined}>{readout ? readout.duration : '—'}</dd>
                </div>
                <div>
                  <dt>Stops around</dt>
                  <dd>{readout ? formatStopTime(readout.stopsAt) : '—'}</dd>
                </div>
              </>
            ) : null}
          </dl>
          {runTime ? null : <EscrowSteps />}

          {/*
            Three states, not one button with three moods. An expired quote is
            a different screen from a live one: the price shown is no longer
            held by anyone, so it is removed rather than greyed out beside a
            dead button.
          */}
          {priceError ? (
            <div className="notice notice-caution" role="alert">
              <strong>The provider price changed</strong>
              <p>{priceError}</p>
              {onRequote ? <Button variant="primary" onClick={onRequote}>Get a new price</Button> : null}
            </div>
          ) : !quote ? (
            <div className="notice notice-caution" role="status">
              <strong>No price yet</strong>
              <p>Ask this provider for a price before signing.</p>
              {onRequote ? <Button onClick={onRequote}>Get a price</Button> : null}
            </div>
          ) : !heightKnown ? (
            <div className="notice notice-caution" role="status">
              <strong>Checking whether this price is still held</strong>
              <p>
                The console cannot read the chain height right now, so it cannot
                tell whether this price is still valid. Signing now could waste an
                approval.
              </p>
            </div>
          ) : !live ? (
            <div className="notice notice-caution" role="alert">
              <strong>This price has expired</strong>
              <p>
                The provider held it for a limited time and that time has passed. No
                capacity is reserved for you now. Ask for a new price — it may
                differ.
              </p>
              {onRequote ? (
                <Button variant="primary" onClick={onRequote}>
                  Get a new price
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="review-actions rent-review-actions">
              <Button variant="primary" disabled={blocked} onClick={onApprove}>
                {label}
              </Button>
              {/*
                The countdown sits next to the button: it is the reason to
                decide now, and the number that changes while the tenant reads.
                At zero the chain has probably not caught up yet, but the price
                is as good as gone, so the pill becomes the way to a new one.
              */}
              {holdOver ? (
                <button type="button" className="hold-pill hold-pill--over" onClick={onRequote}>Get a new price</button>
              ) : (
                <span className="hold-pill" role="timer" aria-live="off">Price held for {formatHold(holdSeconds)}</span>
              )}
              {onBack ? <Button onClick={onBack}>Back</Button> : null}
            </div>
          )}
        </Card>
      </aside>
    </section>
  )
}
